import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  User, Phone, Calendar as CalendarIcon, DollarSign, 
  TrendingUp, Plus, Wallet, Coffee, Home, Truck, Edit3, Save, X, Clock, Trash2, CheckCircle, FileText
} from "lucide-react";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function SupervisorDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [supervisor, setSupervisor] = useState({});
  const [logs, setLogs] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  
  // UI States
  const [showCalendar, setShowCalendar] = useState(false);
  const [isEditingSalary, setIsEditingSalary] = useState(false);
  const [newSalary, setNewSalary] = useState(0);
  const [editingLogId, setEditingLogId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Report Modal States
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportConfig, setReportConfig] = useState({
    type: "range", // 'single' | 'range' | 'full'
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0]
  });

  const [stats, setStats] = useState({
    totalFood: 0,
    totalAdvance: 0,
    totalSalaryPaid: 0,
    pendingSalary: 0,
    totalLeaves: 0,
    totalExpenses: 0,
    currentMonthPresent: 0
  });

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    work_done_description: "",
    food_expenses: 0,
    advance_taken: 0,
    room_expenses: 0,
    travel_expenses: 0,
    other_expenses: 0,
    salary_credited: 0,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: sup } = await supabase.from("supervisors").select("*").eq("id", id).single();
      const { data: logsData } = await supabase.from("supervisor_logs")
        .select("*")
        .eq("supervisor_id", id)
        .order("date", { ascending: false });
      const { data: attData } = await supabase.from("attendance")
        .select("date")
        .eq("supervisor_id", id);

      setSupervisor(sup);
      setNewSalary(sup?.base_monthly_salary || 0);
      setLogs(logsData || []);
      setAttendanceRecords(attData?.map(a => a.date) || []);
      calculateStats(sup, logsData || [], attData || []);
    } catch (error) {
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (sup, logsData, attData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const totals = logsData.reduce((acc, curr) => {
      acc.food += Number(curr.food_expenses || 0);
      acc.advance += Number(curr.advance_taken || 0);
      acc.salaryPaid += Number(curr.salary_credited || 0);
      acc.others += Number(curr.room_expenses || 0) + Number(curr.travel_expenses || 0) + Number(curr.other_expenses || 0);
      return acc;
    }, { food: 0, advance: 0, salaryPaid: 0, others: 0 });

    const monthPresent = attData.filter(a => {
        const d = new Date(a.date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).length;

    const netContractSalary = Number(sup?.base_monthly_salary || 0);
    const calculatedPendingBalance = netContractSalary - totals.advance - totals.salaryPaid;

    setStats({
      totalFood: totals.food,
      totalAdvance: totals.advance,
      totalSalaryPaid: totals.salaryPaid,
      totalLeaves: 0,
      pendingSalary: calculatedPendingBalance,
      totalExpenses: totals.food + totals.others,
      currentMonthPresent: monthPresent
    });
  };

  // --- ATTENDANCE LOGIC ---
  const toggleAttendance = async (dateString) => {
    const isPresent = attendanceRecords.includes(dateString);
    try {
      if (isPresent) {
        await supabase.from("attendance").delete().eq("supervisor_id", id).eq("date", dateString);
        setAttendanceRecords(prev => prev.filter(d => d !== dateString));
      } else {
        await supabase.from("attendance").insert([{ supervisor_id: id, date: dateString }]);
        setAttendanceRecords(prev => [...prev, dateString]);
      }
      fetchData(); 
    } catch (err) {
      toast.error("Attendance update failed");
    }
  };

  // --- SALARY CONFIGURATION ---
  const updateSalary = async () => {
    try {
      const { error } = await supabase.from("supervisors").update({ base_monthly_salary: newSalary }).eq("id", id);
      if (error) throw error;
      toast.success("Salary contract updated");
      setIsEditingSalary(false);
      fetchData();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  // --- LOG CRUD ---
  const handleAddLog = async () => {
    try {
      const now = new Date();
      const timeString = now.toTimeString().split(" ")[0];
      
      const { error } = await supabase.from("supervisor_logs").insert([
        { 
          supervisor_id: id, 
          ...formData, 
          date: `${formData.date}T${timeString}` 
        },
      ]);
      if (error) throw error;
      toast.success("Log record created successfully");
      setShowForm(false);
      setFormData({
        date: new Date().toISOString().split("T")[0],
        work_done_description: "",
        food_expenses: 0,
        advance_taken: 0,
        room_expenses: 0,
        travel_expenses: 0,
        other_expenses: 0,
        salary_credited: 0,
      });
      fetchData();
    } catch (err) {
      toast.error("Failed to add log");
    }
  };

  const handleUpdateLog = async () => {
    try {
      const { error } = await supabase.from("supervisor_logs").update(editFormData).eq("id", editingLogId);
      if (error) throw error;
      toast.success("Record changes saved");
      setEditingLogId(null);
      fetchData();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await supabase.from("supervisor_logs").delete().eq("id", logId);
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  // --- REPORT GENERATION LOGIC ---
  const generatePDFReport = () => {
    try {
      // 1. Filter Logs according to selection type
      let filteredLogs = [...logs];
      if (reportConfig.type === "single") {
        filteredLogs = logs.filter(log => log.date.split("T")[0] === reportConfig.startDate);
      } else if (reportConfig.type === "range") {
        filteredLogs = logs.filter(log => {
          const logDate = log.date.split("T")[0];
          return logDate >= reportConfig.startDate && logDate <= reportConfig.endDate;
        });
      }

      if (filteredLogs.length === 0) {
        toast.error("No transactional logs found for the selected period.");
        return;
      }

      // 2. Initialize PDF Layout Document
      const doc = new jsPDF();
      
      // Theme Branding Setup
      doc.setFillColor(0, 84, 166); // EduZap Brand Blue (#0054A6)
      doc.rect(0, 0, 210, 42, "F");

      // Header Meta Text
      doc.setTextColor(255, 255, 255);
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(22);
      doc.text("FINANCIAL STATEMENT & LOGS", 14, 18);
      
      doc.setFont("Helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 14, 25);
      doc.text(`Timeline Scope: ${reportConfig.type.toUpperCase()} (${reportConfig.type === 'full' ? 'All Records' : reportConfig.type === 'single' ? reportConfig.startDate : `${reportConfig.startDate} to ${reportConfig.endDate}`})`, 14, 30);

      // Supervisor Bio Block
      doc.setTextColor(51, 65, 85);
      doc.setFontSize(11);
      doc.setFont("Helvetica", "bold");
      doc.text("SUPERVISOR DOSSIER", 14, 52);
      
      doc.setFont("Helvetica", "normal");
      doc.text(`Name: ${supervisor?.name || "N/A"}`, 14, 59);
      doc.text(`Phone Contact: ${supervisor?.phone || "N/A"}`, 14, 65);
      doc.text(`Base Contract Rate: Rs. ${Number(supervisor?.base_monthly_salary || 0).toLocaleString()}/mo`, 14, 71);

      // Calculate localized aggregated statistics for the filtered range
      const reportTotals = filteredLogs.reduce((acc, curr) => {
        acc.food += Number(curr.food_expenses || 0);
        acc.advance += Number(curr.advance_taken || 0);
        acc.salaryPaid += Number(curr.salary_credited || 0);
        acc.others += Number(curr.room_expenses || 0) + Number(curr.travel_expenses || 0) + Number(curr.other_expenses || 0);
        return acc;
      }, { food: 0, advance: 0, salaryPaid: 0, others: 0 });

      // Range Summary Table Grid Layout
      doc.setFont("Helvetica", "bold");
      doc.text("PERIODICAL FINANCIAL ACCUMULATION", 115, 52);
      doc.setFont("Helvetica", "normal");
      doc.text(`Salary Disbursed: Rs. ${reportTotals.salaryPaid.toLocaleString()}`, 115, 59);
      doc.text(`Advances Awarded: Rs. ${reportTotals.advance.toLocaleString()}`, 115, 65);
      doc.text(`Operational Overheads: Rs. ${(reportTotals.food + reportTotals.others).toLocaleString()}`, 115, 71);

      // Horizontal Divider Line separator
      doc.setDrawColor(226, 232, 240);
      doc.line(14, 78, 196, 78);

      // 3. Assemble Dynamic Rows Array for AutoTable Engine
      const tableRows = filteredLogs.map((log) => {
        const formattedDate = new Date(log.date).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric'
        });
        const operationalExpenses = Number(log.room_expenses || 0) + Number(log.travel_expenses || 0) + Number(log.other_expenses || 0);
        
        return [
          formattedDate,
          log.work_done_description || (Number(log.salary_credited) > 0 ? "Salary Disbursal" : "Site Maintenance Log"),
          Number(log.food_expenses) > 0 ? `Rs. ${Number(log.food_expenses).toLocaleString()}` : "-",
          operationalExpenses > 0 ? `Rs. ${operationalExpenses.toLocaleString()}` : "-",
          Number(log.advance_taken) > 0 ? `Rs. ${Number(log.advance_taken).toLocaleString()}` : "-",
          Number(log.salary_credited) > 0 ? `Rs. ${Number(log.salary_credited).toLocaleString()}` : "-"
        ];
      });

      // 4. Render Layout Table Document Block via Plugin Engine
    autoTable(doc,{
        startY: 84,
        head: [['Log Date', 'Operational Details / Remarks', 'Food Costs', 'Other Expenses', 'Advances (-)', 'Salary Paid (+)']],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [0, 84, 166], fontSize: 9, fontStyle: 'bold' },
        bodyStyles: { fontSize: 9, textColor: [51, 65, 85] },
        columnStyles: {
          0: { cellWidth: 26 },
          1: { cellWidth: 60 },
          2: { cellWidth: 24, halign: 'right' },
          3: { cellWidth: 26, halign: 'right' },
          4: { cellWidth: 24, halign: 'right' },
          5: { cellWidth: 26, halign: 'right' }
        },
        styles: { overflow: 'linebreak', cellPadding: 3 }
      });

      // Save system downloaded asset string reference target
      const sanitizedName = supervisor?.name?.replace(/\s+/g, '_') || "Supervisor";
      doc.save(`Report_${sanitizedName}_${reportConfig.startDate}.pdf`);
      toast.success("PDF document compiled and downloaded successfully!");
      setReportModalOpen(false);
    } catch (pdfError) {
      console.error(pdfError);
      toast.error("Internal compiler fault generated while assembling export layout.");
    }
  };

  if (loading || !supervisor) return <div className="p-10 text-center font-black animate-pulse text-[#0054A6]">LOADING...</div>;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
      
      {/* 🔷 HEADER SECTION */}
      <div className="bg-[#0054A6] p-8 rounded-[2.5rem] text-white shadow-xl flex flex-col md:flex-row justify-between items-start gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3">
             <h1 className="text-3xl font-black">{supervisor?.name}</h1>
             <div className="bg-green-400 text-[#0054A6] text-[10px] px-2 py-1 rounded-lg font-black uppercase">Active</div>
          </div>
          
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex items-center gap-2 text-xs font-bold bg-white/10 px-3 py-1.5 rounded-full">
              <Phone size={14} /> {supervisor?.phone || "No Phone"}
            </div>
            
            <button 
                onClick={() => setShowCalendar(true)}
                className="flex items-center gap-2 text-xs font-black bg-white text-[#0054A6] px-4 py-1.5 rounded-full hover:bg-green-50 transition-colors shadow-lg"
            >
              <CheckCircle size={14} /> Mark Attendance
            </button>

            {/* Added: Generate Report Trigger Button */}
            <button 
                onClick={() => setReportModalOpen(true)}
                className="flex items-center gap-2 text-xs font-black bg-emerald-500 text-white px-4 py-1.5 rounded-full hover:bg-emerald-600 transition-colors shadow-lg"
            >
              <FileText size={14} /> Generate Report
            </button>

            <div className="flex items-center gap-2 text-xs font-bold bg-white/10 px-3 py-1.5 rounded-full text-green-300">
              <TrendingUp size={14} /> {stats.currentMonthPresent} Days This Month
            </div>
          </div>
        </div>

        <div className="bg-white/10 p-5 rounded-3xl backdrop-blur-md min-w-[220px] border border-white/20">
          <div className="flex justify-between items-center mb-1">
            <p className="text-[10px] uppercase font-black text-blue-200 tracking-widest">Monthly Base Salary</p>
            <button onClick={() => setIsEditingSalary(!isEditingSalary)} className="text-blue-200 hover:text-white">
               {isEditingSalary ? <X size={14}/> : <Edit3 size={14}/>}
            </button>
          </div>
          {isEditingSalary ? (
            <div className="flex gap-2 mt-2">
              <input type="number" value={newSalary} onChange={(e) => setNewSalary(e.target.value)} className="bg-white/20 rounded-lg px-2 py-1 w-full text-lg font-bold outline-none border border-white/30 text-white" />
              <button onClick={updateSalary} className="bg-green-500 p-2 rounded-lg hover:bg-green-600 transition-colors"><Save size={16}/></button>
            </div>
          ) : (
            <p className="text-2xl font-black mt-1">₹{Number(supervisor.base_monthly_salary || 0).toLocaleString()}</p>
          )}
        </div>
      </div>

      {/* 🔷 STATS CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Net Balance Due" value={stats.pendingSalary} color={stats.pendingSalary < 0 ? "text-orange-600" : "text-red-500"}  icon={<Wallet size={16}/>} />
        <StatCard label="Total Advance Taken" value={stats.totalAdvance} color="text-orange-500"  icon={<DollarSign size={16}/>} />
        <StatCard label="Salary Paid Out" value={stats.totalSalaryPaid} color="text-blue-600"  icon={<CheckCircle size={16}/>} />
        <StatCard label="Food Expenses" value={stats.totalFood} color="text-emerald-600" icon={<Coffee size={16}/>} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-[2rem] border shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">Expense & Advance History</h3>
            <button onClick={() => setShowForm(!showForm)} className="bg-[#0054A6] text-white px-4 py-2 rounded-xl text-xs font-black shadow-lg">
              <Plus size={14} className="inline mr-1" /> NEW LOG / CREDIT
            </button>
          </div>

          {showForm && (
            <div className="mb-6 p-6 bg-blue-50/30 rounded-[1.5rem] border border-blue-100 space-y-4 animate-in fade-in zoom-in-95">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Log Date</label>
                      <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 outline-none font-bold text-sm bg-white" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Remarks / Work Description</label>
                      <input placeholder="E.g., Site clearing / Payout reference" value={formData.work_done_description} onChange={(e) => setFormData({...formData, work_done_description: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 outline-none font-bold text-sm bg-white" />
                    </div>
                </div>
                
                <div className="border-t border-slate-200/60 my-2 pt-2">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Transaction Values (₹)</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <input type="number" placeholder="Food Expense" value={formData.food_expenses} onChange={(e) => setFormData({...formData, food_expenses: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 text-sm font-bold bg-white" />
                      <input type="number" placeholder="Room Expense" value={formData.room_expenses} onChange={(e) => setFormData({...formData, room_expenses: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 text-sm font-bold bg-white" />
                      <input type="number" placeholder="Travel Expense" value={formData.travel_expenses} onChange={(e) => setFormData({...formData, travel_expenses: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 text-sm font-bold bg-white" />
                      <input type="number" placeholder="Other Expense" value={formData.other_expenses} onChange={(e) => setFormData({...formData, other_expenses: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 text-sm font-bold bg-white" />
                      
                      <input type="number" placeholder="Advance Taken (-)" value={formData.advance_taken} onChange={(e) => setFormData({...formData, advance_taken: e.target.value})} className="p-3 rounded-xl ring-1 ring-red-100 bg-red-50/50 text-red-600 text-sm font-bold" />
                      <input type="number" placeholder="Salary Credited (+)" value={formData.salary_credited} onChange={(e) => setFormData({...formData, salary_credited: e.target.value})} className="p-3 rounded-xl ring-1 ring-green-100 bg-green-50/50 text-green-600 text-sm font-bold" />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                   <button onClick={handleAddLog} className="bg-[#0054A6] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-md">Confirm Transaction</button>
                </div>
            </div>
          )}

          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col gap-4">
                  {editingLogId === log.id ? (
                      <div className="grid grid-cols-2 gap-3 animate-in fade-in">
                          <input type="date" value={editFormData.date?.split('T')[0]} onChange={(e) => setEditFormData({...editFormData, date: e.target.value})} className="p-2 rounded-lg ring-1 ring-slate-200 text-xs font-bold" />
                          <input value={editFormData.work_done_description} onChange={(e) => setEditFormData({...editFormData, work_done_description: e.target.value})} className="p-2 rounded-lg ring-1 ring-slate-200 text-xs font-bold" />
                          <div className="flex flex-wrap gap-2 col-span-2">
                             <input type="number" value={editFormData.food_expenses} onChange={(e) => setEditFormData({...editFormData, food_expenses: e.target.value})} className="p-2 rounded-lg ring-1 ring-slate-200 text-xs font-bold w-[22%]" placeholder="Food" />
                             <input type="number" value={editFormData.room_expenses} onChange={(e) => setEditFormData({...editFormData, room_expenses: e.target.value})} className="p-2 rounded-lg ring-1 ring-slate-200 text-xs font-bold w-[22%]" placeholder="Room" />
                             <input type="number" value={editFormData.advance_taken} onChange={(e) => setEditFormData({...editFormData, advance_taken: e.target.value})} className="p-2 rounded-lg ring-1 ring-red-200 text-xs font-bold w-[22%]" placeholder="Advance" />
                             <input type="number" value={editFormData.salary_credited} onChange={(e) => setEditFormData({...editFormData, salary_credited: e.target.value})} className="p-2 rounded-lg ring-1 ring-green-200 text-xs font-bold w-[22%]" placeholder="Salary Paid" />
                             
                             <div className="flex gap-1 w-full justify-end mt-2">
                               <button onClick={handleUpdateLog} className="bg-green-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 text-xs font-bold"><Save size={12}/> Save</button>
                               <button onClick={() => setEditingLogId(null)} className="bg-slate-400 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 text-xs font-bold"><X size={12}/> Cancel</button>
                             </div>
                          </div>
                      </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${Number(log.salary_credited) > 0 ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-[#0054A6]'}`}><Clock size={18}/></div>
                            <div>
                                <p className="font-black text-sm text-slate-800">{new Date(log.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                <p className="text-[10px] text-slate-400 font-bold uppercase truncate max-w-[200px]">{log.work_done_description || (Number(log.salary_credited) > 0 ? "Salary Disbursal" : "Expense Log")}</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 items-center justify-between sm:justify-end">
                            {Number(log.food_expenses) > 0 && <LogSubStat label="Food" val={log.food_expenses} />}
                            {Number(log.room_expenses || log.travel_expenses || log.other_expenses) > 0 && (
                              <LogSubStat label="Other Exp" val={Number(log.room_expenses || 0) + Number(log.travel_expenses || 0) + Number(log.other_expenses || 0)} />
                            )}
                            {Number(log.advance_taken) > 0 && <LogSubStat label="Advance" val={log.advance_taken} color="text-red-500" />}
                            {Number(log.salary_credited) > 0 && <LogSubStat label="Salary Paid" val={log.salary_credited} color="text-green-600" />}
                            
                            <div className="flex gap-1 border-l pl-2 border-slate-200">
                                <button onClick={() => {setEditingLogId(log.id); setEditFormData(log);}} className="p-2 text-slate-300 hover:text-blue-600"><Edit3 size={14}/></button>
                                <button onClick={() => handleDeleteLog(log.id)} className="p-2 text-slate-300 hover:text-red-600"><Trash2 size={14}/></button>
                            </div>
                        </div>
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Sidebar Totals Panel */}
        <div className="space-y-6">
            <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white space-y-6 shadow-xl">
                <h3 className="text-xs font-black uppercase text-slate-500 tracking-widest">Financial Ledger Breakdown</h3>
                <div className="space-y-4">
                    <BreakdownRow label="Base Contract Rate" value={Number(supervisor.base_monthly_salary || 0)} />
                    <BreakdownRow label="Advance Deductions (-)" value={stats.totalAdvance} color="text-red-400" />
                    <BreakdownRow label="Salary Transferred (-)" value={stats.totalSalaryPaid} color="text-green-400" />
                    <BreakdownRow label="Total Leaves" value={stats.totalLeaves} color="text-blue-400" />
                    <div className="border-t border-white/10 pt-2">
                       <BreakdownRow label="Total Site Food Costs" value={stats.totalFood} />
                    </div>
                </div>
                <div className="pt-6 border-t border-white/10">
                    <p className="text-[10px] font-black text-slate-500 uppercase mb-1">Net Balance Remaining Due</p>
                    <p className={`text-3xl font-black ${stats.pendingSalary < 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                      ₹{stats.pendingSalary.toLocaleString()}
                    </p>
                </div>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-[2rem] border border-blue-100">
                <h4 className="text-xs font-black text-blue-900 uppercase mb-3 flex items-center gap-2"><TrendingUp size={14}/> Operational Status</h4>
                <p className="text-sm text-blue-800 font-medium leading-relaxed">
                    Logged active attendance for <b>{stats.currentMonthPresent} shifts</b> during this calendar month.
                </p>
            </div>
        </div>
      </div>

      {/* 🔷 ATTENDANCE CALENDAR MODAL */}
      {showCalendar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden">
            <div className="bg-[#0054A6] p-6 text-white flex justify-between items-center">
               <h2 className="text-xl font-black">Mark Attendance</h2>
               <button onClick={() => setShowCalendar(false)} className="hover:rotate-90 transition-transform"><X /></button>
            </div>
            <div className="p-8 grid grid-cols-7 gap-2 text-center">
                {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <div key={d} className="text-[10px] font-black text-slate-300 mb-2 uppercase">{d}</div>)}
                {Array.from({length: 31}, (_, i) => {
                    const day = i + 1;
                    const dateStr = `2026-05-${day.toString().padStart(2, '0')}`;
                    const isPresent = attendanceRecords.includes(dateStr);
                    return (
                        <button 
                            key={day}
                            onClick={() => toggleAttendance(dateStr)}
                            className={`aspect-square rounded-xl text-xs font-black transition-all ${isPresent ? 'bg-green-500 text-white shadow-lg shadow-green-100' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>
            <div className="px-8 pb-8 text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">Tap dates to toggle presence (Green = Present)</p>
            </div>
          </div>
        </div>
      )}

      {/* 🔷 REPORT CONFIGURATION & EXPORT MODAL */}
      {reportModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <h2 className="text-2xl font-black text-slate-900 mb-2">Generate Report</h2>
            <p className="text-slate-500 text-sm mb-8 font-medium">Select the data range for the PDF export.</p>

            <div className="space-y-6">
              {/* Type Selector */}
              <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                {['single', 'range', 'full'].map((t) => (
                  <button
                    key={t}
                    onClick={() => setReportConfig({ ...reportConfig, type: t })}
                    className={`flex-1 py-2 text-[10px] font-black uppercase rounded-xl transition-all ${
                      reportConfig.type === t ? 'bg-white text-[#0054A6] shadow-sm' : 'text-slate-400'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Date Inputs */}
              {reportConfig.type !== 'full' && (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase ml-2 mb-1 block">
                      {reportConfig.type === 'range' ? 'Start Date' : 'Select Date'}
                    </label>
                    <input
                      type="date"
                      value={reportConfig.startDate}
                      onChange={(e) => setReportConfig({ ...reportConfig, startDate: e.target.value })}
                      className="w-full bg-slate-50 border-none rounded-2xl font-bold p-4 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {reportConfig.type === 'range' && (
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase ml-2 mb-1 block">End Date</label>
                      <input
                        type="date"
                        value={reportConfig.endDate}
                        onChange={(e) => setReportConfig({ ...reportConfig, endDate: e.target.value })}
                        className="w-full bg-slate-50 border-none rounded-2xl font-bold p-4 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>
              )}

              {reportConfig.type === 'full' && (
                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
                  <p className="text-blue-700 text-xs font-bold text-center">
                    This will aggregate all historical logs for this site into one document.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setReportModalOpen(false)}
                  className="flex-1 py-4 text-slate-500 font-black text-xs uppercase hover:bg-slate-50 rounded-2xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={generatePDFReport}
                  className="flex-[2] py-4 bg-[#0054A6] text-white font-black text-xs uppercase rounded-2xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- HELPERS ---
function StatCard({ label, value, color, icon }) {
  return (
    <div className="bg-white p-5 rounded-3xl border shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2 text-slate-400 mb-2">{icon}<p className="text-[10px] font-black uppercase tracking-wider">{label}</p></div>
      <p className={`text-xl font-black ${color}`}>₹{Number(value || 0).toLocaleString()}</p>
    </div>
  );
}
function LogSubStat({ label, val, color = "text-slate-700" }) {
  return (
    <div className="text-center min-w-[55px]">
      <p className="text-[9px] font-black text-slate-400 uppercase">{label}</p>
      <p className={`text-xs font-black ${color}`}>₹{Number(val || 0).toLocaleString()}</p>
    </div>
  );
}
function BreakdownRow({ label, value, color = "text-white" }) {
  return (
    <div className="flex justify-between items-center text-sm font-bold">
      <span className="text-slate-400">{label}</span>
      <span className={color}>₹{value.toLocaleString()}</span>
    </div>
  );
}